import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { GET_REWARD_FILTERS } from "../../../graphql/resolvers/reward";
import { I18N_NAMESPACES } from "../../../helpers/constants";
import {
  RewardsStoreFilters,
  REWARD_POINTS_RANGES,
  getRewardPointsBounds,
  getSelectedRewardPointRanges,
} from "../../../pages/RewardsStore/helpers";
import { RewardFilters } from "../../../types/__generated__/RewardFilters";
import { colors } from "../../../styles/variables";
import { InterrogationRadioComponent } from "../../molecules/FilterWinesHolder/style";
import Button from "../../atoms/Button";
import SideTray from "../../atoms/SideTray";
import {
  Form,
  Header,
  Title,
  CloseButton,
  FilterGroups,
  FilterGroup,
  GroupTitle,
  PointsOptions,
  Colour,
  Footer,
} from "./style";

interface RewardsFilterTrayProps {
  filters: RewardsStoreFilters;
  onApply: (filters: RewardsStoreFilters) => void;
  onClose: () => void;
}

const RewardsFilterTray = ({ filters, onApply, onClose }: RewardsFilterTrayProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.COMMON, I18N_NAMESPACES.REWARDS]);
  const [pointRanges, setPointRanges] = useState(() => getSelectedRewardPointRanges(filters));
  const [colourIds, setColourIds] = useState(filters.colourIds);
  const closeButton = useRef<HTMLButtonElement>(null);
  const { data, loading, error, refetch } = useQuery<RewardFilters>(GET_REWARD_FILTERS);

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    closeButton.current?.focus({ preventScroll: true });
    return () => previousFocus?.focus({ preventScroll: true });
  }, []);

  const clear = () => {
    setPointRanges([]);
    setColourIds([]);
  };

  return (
    <SideTray isOpened color={colors.white} onClickBackdrop={onClose} shouldAnimate>
      <Form
        role="dialog"
        aria-modal="true"
        aria-labelledby="rewards-filter-title"
        onKeyDown={(event) => {
          if (event.key === "Escape") onClose();
        }}
        onSubmit={(event) => {
          event.preventDefault();
          onApply({
            ...getRewardPointsBounds(pointRanges),
            pointRanges,
            colourIds,
          });
        }}
      >
        <Header>
          <Title id="rewards-filter-title">{t("rewards:store.filters")}</Title>
          <CloseButton
            ref={closeButton}
            type="button"
            onClick={onClose}
            aria-label={t("rewards:store.closeFilters")}
          >
            ×
          </CloseButton>
        </Header>
        <FilterGroups>
          <FilterGroup role="group" aria-labelledby="rewards-points-range-title">
            <GroupTitle id="rewards-points-range-title">{t("rewards:store.pointsRange")}</GroupTitle>
            <PointsOptions
              type="checkbox"
              onChange={(id: number) =>
                setPointRanges((selected) =>
                  selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id]
                )
              }
            >
              {REWARD_POINTS_RANGES.map((range) => (
                <InterrogationRadioComponent
                  key={range.id}
                  value={range.id}
                  label={t("rewards:store.pointsOption", { context: String(range.id) })}
                  forceChecked={pointRanges.includes(range.id)}
                />
              ))}
            </PointsOptions>
          </FilterGroup>
          <FilterGroup role="group" aria-labelledby="rewards-colour-title">
            <GroupTitle id="rewards-colour-title">{t("common:colour")}</GroupTitle>
            {loading && <p role="status">{t("common:loading")}</p>}
            {error && (
              <p role="alert">
                {t("rewards:store.filtersError")}
                <Button type="button" onClick={() => refetch()}>
                  {t("rewards:store.retry")}
                </Button>
              </p>
            )}
            {data?.allFreebieColours?.map((colour) => {
              const { id, name } = colour || {};
              if (id == null) return null;
              return (
                <Colour
                  key={id}
                  value={id}
                  multipleSelection
                  isChecked={colourIds.includes(id)}
                  onChange={() =>
                    setColourIds((selected) =>
                      selected.includes(id)
                        ? selected.filter((selectedId) => selectedId !== id)
                        : [...selected, id]
                    )
                  }
                >
                  {name}
                </Colour>
              );
            })}
          </FilterGroup>
        </FilterGroups>
        <Footer>
          <Button type="button" ghost onClick={clear}>
            {t("common:clear")}
          </Button>
          <Button type="submit">{t("common:done")}</Button>
        </Footer>
      </Form>
    </SideTray>
  );
};

export default RewardsFilterTray;
